import { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { styled } from '@material-ui/core/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RemoveIcon from '@mui/icons-material/Remove';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  Typography,
  Button,
  CardMedia,
  Avatar,
  Stack,
  IconButton,
  Grid
} from '@material-ui/core';
import LoadingSkeleton from './LoadingSkeleton';
import { useThemeMode } from '../../theme';

const helpSiteBase = '/static/help-system-guide/';

const defaultHelpTopic = 'virtual_rack_home.html';

// Sidebar widths — full on lg+ where there's a 280px DashboardSidebar alongside,
// narrower on smaller breakpoints so the help panel doesn't dominate the screen.
const SIDEBAR_WIDTH_LG = 450;
const SIDEBAR_WIDTH_SM = 360;

function topicPathFromStep(step) {
  if (!step) {
    return defaultHelpTopic;
  }

  const topic = step
    .replace(/^\//, '')
    .replace(/\.htm$/i, '.html')
    .replace(/\.md$/i, '.html');
  return topic.endsWith('.html') ? topic : `${topic}.html`;
}

// Ordered route-to-help-topic rules. Each entry is tested in sequence; the
// first match wins. `match` is called with the current pathname. Keep more
// specific patterns before broader ones (e.g. `/billing/company/` before
// `/billing`, `/team-usage/` before `/team-usage`).
const ROUTE_HELP_TOPICS = [
  { match: (p) => p.startsWith('/admin/companies'), doc: 'company_management.html' },
  { match: (p) => p.includes('/part/'), doc: 'part_number_tracking.html' },
  {
    match: (p) => p.includes('/team/upload') || p.includes('/upload'),
    doc: 'Loading/loadables.html'
  },
  { match: (p) => p.includes('/team/'), doc: 'team_usage_detail.html' },
  { match: (p) => p.startsWith('/billing/company/'), doc: 'company_billing_report.html' },
  { match: (p) => p.startsWith('/billing'), doc: 'billing.html' },
  { match: (p) => p.startsWith('/team-usage/'), doc: 'team_usage_detail.html' },
  { match: (p) => p.startsWith('/team-usage'), doc: 'team_usage_report.html' },
  { match: (p) => p.startsWith('/snapshots'), doc: 'snapshots.html' },
  { match: (p) => p.startsWith('/vmtemplates'), doc: 'vm_templates.html' },
  { match: (p) => p.startsWith('/teams'), doc: 'team_management.html' },
  { match: (p) => p.includes('/racks/details'), doc: 'vrack_details.html' },
  { match: (p) => p.includes('/racks'), doc: defaultHelpTopic }
];

function resolveHelpTopic(pathname) {
  const entry = ROUTE_HELP_TOPICS.find((rule) => rule.match(pathname));
  return entry ? entry.doc : defaultHelpTopic;
}

// Wrapper that sits in the dashboard's flex row. Collapses to width 0 when the
// panel is hidden so the avatar (position: fixed) stays anchored to the viewport
// corner without reserving space.
const SidebarWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  width: 0,
  transition: theme.transitions.create('width'),
  overflow: 'hidden',
  ...(open && {
    width: SIDEBAR_WIDTH_SM,
    [theme.breakpoints.up('lg')]: {
      width: SIDEBAR_WIDTH_LG
    }
  })
}));

const PanelShell = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  borderLeft: `1px solid ${theme.palette.divider}`,
  boxShadow: '-6px 0 18px rgba(34, 47, 62, 0.12)'
}));

const avatarStyle = {
  position: 'fixed',
  zIndex: 99999,
  bottom: 20,
  right: 20,
  padding: 0,
  margin: 0,
  minWidth: 40,
  height: 40,
  backgroundColor: 'primary.light',
  '&:hover': { backgroundColor: 'primary.main' }
};

export default function HelpSystem() {
  const { mode } = useThemeMode();
  const [currentDocument, setCurrentDocument] = useState(defaultHelpTopic);
  const [searchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const setHelpPage = () => {
      // Set current document based on page the user is on
      if (location.search !== ' ') {
        if (searchParams.get('ssh_open')) {
          setCurrentDocument('connect_to_vrack.html');
        } else if (searchParams.get('new_component')) {
          setCurrentDocument('Loading/add_new_component.html');
        } else if (searchParams.get('loading_open')) {
          setCurrentDocument('Loading/load_vrack.html');
        } else if (searchParams.get('step')) {
          setCurrentDocument(topicPathFromStep(searchParams.get('step')));
        } else if (searchParams.get('flight_data_open')) {
          setCurrentDocument('aircraft_data.html');
        } else if (location.pathname) {
          setCurrentDocument(resolveHelpTopic(location.pathname));
        }
      }
    };

    if (location !== null) {
      setHelpPage();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [location]);

  const helpUrl = `${helpSiteBase}${currentDocument}`;
  const embeddedHelpUrl = `${helpUrl.replace(/\.html$/, '.htm')}?embed=1&theme=${mode}`;

  return (
    <>
      {collapsed && (
        <Avatar
          data-testid="help-button"
          component={Button}
          onClick={() => {
            setCollapsed(false);
            setLoaded(false);
          }}
          sx={avatarStyle}
        >
          <HelpOutlineIcon fontSize="large" />
        </Avatar>
      )}
      <SidebarWrapper open={!collapsed} aria-hidden={collapsed}>
        {!collapsed && (
          <PanelShell>
            <Box
              sx={{
                padding: 1,
                borderBottom: '3px solid',
                borderColor: 'primary.main'
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ paddingLeft: 1 }} variant="h6">
                  Virtual Rack Help
                </Typography>
                <Stack direction="row" alignItems="center">
                  <IconButton
                    onClick={() => {
                      window.open(helpUrl);
                    }}
                    aria-label="maximize"
                  >
                    <OpenInNewIcon />
                  </IconButton>

                  <IconButton
                    onClick={() => {
                      setCollapsed(true);
                    }}
                    aria-label="minimize"
                  >
                    <RemoveIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Box>

            <Grid item xs sx={{ overflow: 'auto', flex: 1 }}>
              {!loaded && <LoadingSkeleton />}
              <CardMedia
                component="iframe"
                sx={{
                  width: '100%',
                  height: '100%',
                  display: !loaded ? 'none' : '',
                  border: 'none',
                  boxSizing: 'border-box'
                }}
                onLoad={() => {
                  setLoaded(true);
                }}
                image={embeddedHelpUrl}
              />
            </Grid>
          </PanelShell>
        )}
      </SidebarWrapper>
    </>
  );
}
